/**
 * EmailPreviewModal — trial mode's "nothing was sent" preview (04 §4.8).
 * A single instance is mounted in the app shell; any send call site opens it
 * via emailPreviewStore.showEmailPreview(). Renders the suppressed email's HTML
 * in a sandboxed iframe (srcDoc, no scripts) so untrusted rendered markup can
 * never touch the app, alongside its subject and recipient.
 */
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from "@mui/material";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import { doc, getDoc } from "firebase/firestore";
import { useAuth } from "../../services/use-auth";
import { useEmailPreview, closeEmailPreview } from "./emailPreviewStore";

const BANNER_LINE =
  "Nothing was sent — trial mode shows you what recipients would get.";

export default function EmailPreviewModal() {
  const { open, previewId, orgId, preview: seeded } = useEmailPreview();
  const { db } = useAuth();
  const [preview, setPreview] = useState(seeded);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    if (seeded) {
      setPreview(seeded);
      return undefined;
    }
    if (!db || !orgId || !previewId) {
      setPreview(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    getDoc(doc(db, `orgs/${orgId}/emailPreviews`, previewId))
      .then((snap) => {
        if (!cancelled) setPreview(snap.exists() ? snap.data() : null);
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, seeded, db, orgId, previewId]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={closeEmailPreview}
      maxWidth="md"
      fullWidth
      data-testid="email-preview-modal"
      aria-label="Email preview">
      <DialogTitle sx={{ pb: 1 }}>Email preview</DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
            p: 1.25,
            borderRadius: 1,
            bgcolor: "action.hover",
            fontSize: "0.85rem",
          }}
          data-testid="email-preview-banner-line">
          <MarkEmailReadRoundedIcon fontSize="small" color="primary" />
          <span>{BANNER_LINE}</span>
        </Box>

        {loading ? (
          <Typography variant="body2" color="text.secondary" data-testid="email-preview-loading">
            Loading preview…
          </Typography>
        ) : preview ? (
          <>
            <Typography variant="body2" sx={{ mb: 0.5 }} data-testid="email-preview-subject">
              <strong>Subject:</strong> {preview.subject || "(no subject)"}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }} data-testid="email-preview-to">
              <strong>To:</strong>{" "}
              {Array.isArray(preview.to) ? preview.to.join(", ") : preview.to || "(no recipient)"}
            </Typography>
            <Box
              component="iframe"
              title="Email preview"
              data-testid="email-preview-iframe"
              // Sandboxed with NO allow-scripts token — the rendered email HTML
              // is inert; it cannot run JS or navigate the parent.
              sandbox=""
              srcDoc={preview.html || "<p>(empty email)</p>"}
              sx={{
                width: "100%",
                minHeight: 360,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "#fff",
              }}
            />
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" data-testid="email-preview-empty">
            This preview is no longer available.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeEmailPreview} data-testid="email-preview-close">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
